import { Plugin } from '@tiptap/pm/state'
import { fixTablesKey, tableEditing } from '@tiptap/pm/tables'

/**
 * Tables whose rows skip grid columns (RED-317 PR 3b).
 *
 * A Word row may start or end beside the grid: `w:gridBefore`/`w:gridAfter`
 * in its trPr (11 of the 47 malls in storage/template/2). The editor keeps
 * such a row as it is, with fewer cells than the table is wide, and the
 * compiler keeps its grid (contract §4). prosemirror-tables reads the short
 * row as missing cells, though: its fixTables pass appends empty cells to it
 * after any edit inside the table (seen in the browser on the Tallinn
 * korteriomand mall's "Tabel 1": one keystroke turned a 13-cell gridAfter
 * row into 14 cells, one more than its grid), and its row/column commands
 * read the missing positions as the table's first row. So for these tables:
 * the fix-up is skipped (studioTableEditing) and the structure commands
 * refuse (GRID_GUARDED_COMMANDS); text, cell fill, deleting a row and
 * deleting the table work as anywhere else.
 */

/** Does any row of this table node skip grid columns? */
export function skipsGridColumns(table) {
  let skips = false
  table.forEach((row) => {
    skips ||= /<w:grid(?:Before|After)\b/.test(row.attrs.trPr ?? '')
  })
  return skips
}

/** The table node around the selection's head, or null. */
export function selectedTable(state) {
  const { $head } = state.selection
  for (let depth = $head.depth; depth > 0; depth--) {
    const node = $head.node(depth)
    if (node.type.spec.tableRole === 'table') return node
  }
  return null
}

/** Is the selection inside a table whose rows skip grid columns? */
export function inGridSkippingTable(state) {
  const table = selectedTable(state)
  return table !== null && skipsGridColumns(table)
}

/** Table commands that read the full grid; they refuse inside a grid-skipping table. */
export const GRID_GUARDED_COMMANDS = [
  'addColumnBefore',
  'addColumnAfter',
  'deleteColumn',
  'addRowBefore',
  'addRowAfter',
  'mergeCells',
  'splitCell',
  'mergeOrSplit',
  'toggleHeaderRow',
  'toggleHeaderColumn',
  'toggleHeaderCell',
]

/** Does a fixTables transaction step into a table whose rows skip grid columns? */
function fixesGridSkippingTable(tr) {
  return tr.steps.some((step, index) => {
    if (typeof step.from !== 'number') return false
    const $pos = tr.docs[index].resolve(Math.min(step.from, tr.docs[index].content.size))
    for (let depth = $pos.depth; depth > 0; depth--) {
      const node = $pos.node(depth)
      if (node.type.spec.tableRole === 'table') return skipsGridColumns(node)
    }
    return false
  })
}

/**
 * prosemirror-tables' tableEditing with one change: a fix-up that would add
 * cells to a grid-skipping table is dropped. The selection normalisation it
 * also does still runs (fixTables then sees no changed table).
 */
export function studioTableEditing(options) {
  const base = tableEditing(options)
  return new Plugin({
    ...base.spec,
    appendTransaction(transactions, oldState, state) {
      const tr = base.spec.appendTransaction(transactions, oldState, state)
      if (tr?.getMeta(fixTablesKey) && fixesGridSkippingTable(tr)) {
        return base.spec.appendTransaction(transactions, state, state)
      }
      return tr
    },
  })
}
